import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Container,
  Paper,
  useTheme,
  useMediaQuery,
  Typography,
  Alert,
  TextField,
  Button,
  CircularProgress,
  InputAdornment,
  Divider,
  Stack,
  Link
} from '@mui/material';
import { motion } from 'framer-motion';
import EmailIcon from '@mui/icons-material/Email';
import LockIcon from '@mui/icons-material/Lock';
import PersonIcon from '@mui/icons-material/Person';
import PhoneIcon from '@mui/icons-material/Phone';
import FingerprintIcon from '@mui/icons-material/Fingerprint';
import MobileInput from '../../components/auth/MobileInput';
import OTPInput from '../../components/auth/OTPInput';
import LoginForm from '../../components/auth/LoginForm';
import RegisterForm from '../../components/auth/RegisterForm';
import CreateUsername from '../../components/auth/CreateUsername';
import { useAuth } from '../../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import Header from '../../components/common/Header';
import Footer from '../../components/common/Footer';
import splashImages from '../../mocks/splashImages.json';
import api from '../../services/api';
import md5 from 'md5';
import { Capacitor } from '@capacitor/core';
import { GoogleAuth } from '@codetrix-studio/capacitor-google-auth';
import { useNotification } from '../../contexts/NotificationContext';
import { resolveStoreAssetUrl } from '../../utils/storeAssets';
import {
  isFirebaseSms,
  initRecaptcha,
  sendPhoneOtp,
  verifyPhoneOtp,
} from '../../services/firebase';

// ── WebAuthn helpers ──────────────────────────────────────────────────────────
function b64urlToBuffer(b64url) {
  const base64 = (b64url || '').replace(/-/g, '+').replace(/_/g, '/');
  const padded = base64 + '='.repeat((4 - (base64.length % 4)) % 4);
  const binary = atob(padded);
  const buf = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) buf[i] = binary.charCodeAt(i);
  return buf.buffer;
}
function bufferToB64url(buffer) {
  const bytes = new Uint8Array(buffer);
  let bin = '';
  for (const b of bytes) bin += String.fromCharCode(b);
  return btoa(bin).replace(/\+/g, '-').replace(/\//g, '_').replace(/=/g, '');
}
function assertionToJSON(cred) {
  const r = cred.response;
  return {
    id: cred.id,
    rawId: bufferToB64url(cred.rawId),
    type: cred.type,
    response: {
      clientDataJSON:    bufferToB64url(r.clientDataJSON),
      authenticatorData: bufferToB64url(r.authenticatorData),
      signature:         bufferToB64url(r.signature),
      userHandle:        r.userHandle ? bufferToB64url(r.userHandle) : undefined,
    },
  };
}

// ── Google Identity Services loader (browser/PWA only) ───────────────────────
// google.accounts.oauth2's token client opens Google's consent screen via a
// real window.open() popup - the native app's WebView never registers a
// WebChromeClient.onCreateWindow() handler, so that call is silently dropped,
// and even if it were handled, Google's own policy blocks OAuth flows it
// detects running inside a generic embedded WebView ("disallowed_useragent")
// regardless. Native uses @codetrix-studio/capacitor-google-auth instead (see
// handleGoogleLogin below), which talks to Play Services directly - no
// WebView/browser popup involved at all.
function loadGis() {
  return new Promise((resolve) => {
    if (window.google?.accounts?.oauth2) { resolve(); return; }
    const script = document.createElement('script');
    script.src = 'https://accounts.google.com/gsi/client';
    script.onload = resolve;
    script.onerror = resolve; // non-blocking
    document.head.appendChild(script);
  });
}

// ── Email form component ──────────────────────────────────────────────────────
const GoogleIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" style={{ marginRight: 6 }}>
    <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
    <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
    <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l3.66-2.84z"/>
    <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
  </svg>
);

function EmailAuthForm({ onSignup, onLogin, onForgotReset, onPhoneLogin, onPasskeyLogin, onGoogleLogin, loading }) {
  const [mode, setMode] = useState('email'); // email | signup | signup_otp | login | forgot | forgot_otp
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [otp, setOtp] = useState('');
  const [otpLoading, setOtpLoading] = useState(false);
  const [passkeyPhone, setPasskeyPhone] = useState(null);
  const [passkeyAvailable, setPasskeyAvailable] = useState(false);
  const [pendingSignup, setPendingSignup] = useState(null); // {email, password, name}
  const [pendingReset, setPendingReset] = useState(null);   // {email}
  const [error, setError] = useState('');
  const [resendCooldown, setResendCooldown] = useState(0);

  React.useEffect(() => {
    if (!Capacitor.isNativePlatform()) return;
    GoogleAuth.initialize().catch(() => {});
  }, []);

  React.useEffect(() => {
    const stored = localStorage.getItem('passkey_phone');
    if (!stored || !window.PublicKeyCredential) return;
    window.PublicKeyCredential.isUserVerifyingPlatformAuthenticatorAvailable()
      .then(available => {
        if (available) {
          setPasskeyAvailable(true);
          setPasskeyPhone(stored);
        }
      })
      .catch(() => {});
  }, []);

  // Countdown for resend button
  React.useEffect(() => {
    if (resendCooldown <= 0) return;
    const t = setTimeout(() => setResendCooldown(c => c - 1), 1000);
    return () => clearTimeout(t);
  }, [resendCooldown]);

  const normalizedEmail = () => email.trim().toLowerCase();
  const isValidEmail = (value) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value);

  const handleEmailSubmit = async (e) => {
    e.preventDefault();
    setError('');
    const nextEmail = normalizedEmail();
    if (!nextEmail || !isValidEmail(nextEmail)) {
      setError('Enter a valid email address');
      return;
    }

    setOtpLoading(true);
    try {
      const { exists } = await api.checkEmail(nextEmail);
      setEmail(nextEmail);
      setPassword('');
      setConfirmPassword('');
      setOtp('');
      setMode(exists ? 'login' : 'signup');
    } catch (err) {
      setError(err.message || 'Unable to check that email. Please try again.');
    } finally {
      setOtpLoading(false);
    }
  };

  const handleSignupSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (!email.trim() || !password) { setError('Email and password are required'); return; }
    if (!isValidEmail(email.trim())) { setError('Enter a valid email address'); return; }
    if (password.length < 6) { setError('Password must be at least 6 characters'); return; }
    if (password !== confirmPassword) { setError('Passwords do not match'); return; }
    setOtpLoading(true);
    try {
      await api.sendEmailOtp(normalizedEmail());
      setPendingSignup({ email: normalizedEmail(), password: md5(password), name: name.trim() });
      setOtp('');
      setResendCooldown(30);
      setMode('signup_otp');
    } catch (err) {
      setError(err.message || 'Failed to send verification code');
    } finally {
      setOtpLoading(false);
    }
  };

  const handleOtpSubmit = (e) => {
    e.preventDefault();
    setError('');
    if (!otp || otp.length !== 6) { setError('Enter the 6-digit code from your email'); return; }
    onSignup(pendingSignup.email, pendingSignup.password, pendingSignup.name, otp);
  };

  const handleResend = async () => {
    if (resendCooldown > 0 || !pendingSignup) return;
    setOtpLoading(true);
    try {
      await api.sendEmailOtp(pendingSignup.email);
      setResendCooldown(30);
    } catch (err) {
      setError(err.message || 'Failed to resend code');
    } finally {
      setOtpLoading(false);
    }
  };

  const handleLogin = (e) => {
    e.preventDefault();
    setError('');
    if (!email.trim() || !password) { setError('Email and password are required'); return; }
    onLogin(normalizedEmail(), md5(password));
  };

  const handleForgotSendOtp = async (e) => {
    e.preventDefault();
    setError('');
    if (!email.trim() || !isValidEmail(email.trim())) {
      setError('Enter a valid email address');
      return;
    }
    setOtpLoading(true);
    try {
      await api.sendEmailForgotPassword(normalizedEmail());
      setPendingReset({ email: normalizedEmail() });
      setOtp('');
      setPassword('');
      setConfirmPassword('');
      setResendCooldown(30);
      setMode('forgot_otp');
    } catch (err) {
      setError(err.message || 'Failed to send reset code');
    } finally {
      setOtpLoading(false);
    }
  };

  const handleForgotResend = async () => {
    if (resendCooldown > 0 || !pendingReset) return;
    setOtpLoading(true);
    try {
      await api.sendEmailForgotPassword(pendingReset.email);
      setResendCooldown(30);
    } catch (err) {
      setError(err.message || 'Failed to resend code');
    } finally {
      setOtpLoading(false);
    }
  };

  const handleForgotReset = (e) => {
    e.preventDefault();
    setError('');
    if (!otp || otp.length !== 6) { setError('Enter the 6-digit reset code'); return; }
    if (!password || password.length < 6) { setError('Password must be at least 6 characters'); return; }
    if (password !== confirmPassword) { setError('Passwords do not match'); return; }
    onForgotReset(pendingReset.email, otp, md5(password));
  };

  if (mode === 'email') {
    const maskedPhone = passkeyPhone ? `••••••${passkeyPhone.slice(-4)}` : '';

    return (
      <Box component="form" onSubmit={handleEmailSubmit} sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
        <Typography variant="h5" align="center" fontWeight={700}>Your Events, Understood</Typography>

        {passkeyAvailable && passkeyPhone && (
          <>
            <Button
              variant="contained"
              size="large"
              fullWidth
              startIcon={loading ? <CircularProgress size={20} color="inherit" /> : <FingerprintIcon />}
              onClick={() => onPasskeyLogin(passkeyPhone)}
              disabled={loading}
              sx={{ py: 1.5, background: 'linear-gradient(135deg, #1976d2 0%, #42a5f5 100%)' }}
            >
              Sign in with Fingerprint&nbsp;
              <Typography component="span" variant="caption" sx={{ opacity: 0.85, ml: 0.5 }}>
                ({maskedPhone})
              </Typography>
            </Button>
            <Divider><Typography variant="caption" color="text.secondary">or continue with</Typography></Divider>
          </>
        )}

        <Button
          variant="outlined"
          size="large"
          fullWidth
          onClick={onGoogleLogin}
          disabled={loading}
          sx={{ py: 1.5, borderColor: '#dadce0', color: 'text.primary', '&:hover': { borderColor: '#bbb', bgcolor: 'grey.50' } }}
        >
          <GoogleIcon />
          Continue with Google
        </Button>

        <Divider><Typography variant="caption" color="text.secondary">or use email address</Typography></Divider>

        {error && <Alert severity="error">{error}</Alert>}
        <TextField
          label="Email Address"
          type="email"
          value={email}
          onChange={e => setEmail(e.target.value)}
          fullWidth
          required
          disabled={loading || otpLoading}
          InputProps={{ startAdornment: <InputAdornment position="start"><EmailIcon color="action" /></InputAdornment> }}
        />
        <Button type="submit" variant="contained" size="large" fullWidth disabled={loading || otpLoading}>
          {loading || otpLoading ? <CircularProgress size={24} /> : 'Get Started'}
        </Button>

        <Divider sx={{ my: 0 }} />

        <Button variant="text" size="medium" fullWidth onClick={onPhoneLogin} startIcon={<PhoneIcon />} disabled={loading || otpLoading} sx={{ color: 'text.secondary' }}>
          Login with phone number instead
        </Button>
      </Box>
    );

  }

  if (mode === 'signup') {
    return (
      <Box component="form" onSubmit={handleSignupSubmit} sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        <Typography variant="h6" align="center" fontWeight={700}>Create Account</Typography>
        {error && <Alert severity="error">{error}</Alert>}
        <TextField label="Full Name" value={name} onChange={e => setName(e.target.value)} fullWidth disabled={loading || otpLoading}
          InputProps={{ startAdornment: <InputAdornment position="start"><PersonIcon color="action" /></InputAdornment> }} />
        <TextField label="Email" type="email" value={email} onChange={e => setEmail(e.target.value)} fullWidth required disabled={loading || otpLoading}
          InputProps={{ startAdornment: <InputAdornment position="start"><EmailIcon color="action" /></InputAdornment> }} />
        <TextField label="Password" type="password" value={password} onChange={e => setPassword(e.target.value)} fullWidth required disabled={loading || otpLoading}
          InputProps={{ startAdornment: <InputAdornment position="start"><LockIcon color="action" /></InputAdornment> }} />
        <TextField label="Confirm Password" type="password" value={confirmPassword} onChange={e => setConfirmPassword(e.target.value)} fullWidth required disabled={loading || otpLoading}
          InputProps={{ startAdornment: <InputAdornment position="start"><LockIcon color="action" /></InputAdornment> }} />
        <Button type="submit" variant="contained" size="large" fullWidth disabled={loading || otpLoading}>
          {loading || otpLoading ? <CircularProgress size={24} /> : 'Send Verification Code'}
        </Button>
        <Stack direction="row" justifyContent="space-between">
          <Button variant="text" onClick={() => setMode('email')} sx={{ color: 'text.secondary' }}>← Change email</Button>
          <Button variant="text" onClick={() => setMode('login')} sx={{ color: 'primary.main' }}>Already have an account?</Button>
        </Stack>
      </Box>
    );
  }

  if (mode === 'signup_otp') {
    return (
      <Box component="form" onSubmit={handleOtpSubmit} sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        <Typography variant="h6" align="center" fontWeight={700}>Verify your email</Typography>
        <Alert severity="info" sx={{ fontSize: '0.8rem' }}>
          We sent a 6-digit code to <strong>{pendingSignup?.email}</strong>. Check your inbox (and spam folder).
        </Alert>
        {error && <Alert severity="error">{error}</Alert>}
        <TextField
          label="Verification Code"
          value={otp}
          onChange={e => setOtp(e.target.value.replace(/\D/g, '').slice(0, 6))}
          fullWidth required disabled={loading}
          inputProps={{ inputMode: 'numeric', maxLength: 6 }}
          placeholder="6-digit code"
        />
        <Button type="submit" variant="contained" size="large" fullWidth disabled={loading || otp.length !== 6}>
          {loading ? <CircularProgress size={24} /> : 'Create Account'}
        </Button>
        <Stack direction="row" justifyContent="space-between" alignItems="center">
          <Button variant="text" onClick={() => setMode('signup')} sx={{ color: 'text.secondary' }}>← Change email</Button>
          <Button variant="text" size="small" onClick={handleResend} disabled={resendCooldown > 0 || otpLoading} sx={{ color: 'primary.main' }}>
            {resendCooldown > 0 ? `Resend in ${resendCooldown}s` : 'Resend code'}
          </Button>
        </Stack>
      </Box>
    );
  }

  if (mode === 'forgot') {
    return (
      <Box component="form" onSubmit={handleForgotSendOtp} sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        <Typography variant="h6" align="center" fontWeight={700}>Reset Password</Typography>
        <Typography variant="body2" color="text.secondary" align="center">
          Enter your email and we'll send a reset code.
        </Typography>
        {error && <Alert severity="error">{error}</Alert>}
        <TextField label="Email" type="email" value={email} onChange={e => setEmail(e.target.value)} fullWidth required
          disabled={loading || otpLoading}
          InputProps={{ startAdornment: <InputAdornment position="start"><EmailIcon color="action" /></InputAdornment> }} />
        <Button type="submit" variant="contained" size="large" fullWidth disabled={loading || otpLoading}>
          {loading || otpLoading ? <CircularProgress size={24} /> : 'Send Reset Code'}
        </Button>
        <Button variant="text" onClick={() => setMode('login')} sx={{ color: 'text.secondary' }}>← Back to sign in</Button>
      </Box>
    );
  }

  if (mode === 'forgot_otp') {
    return (
      <Box component="form" onSubmit={handleForgotReset} sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        <Typography variant="h6" align="center" fontWeight={700}>Set New Password</Typography>
        <Alert severity="info" sx={{ fontSize: '0.8rem' }}>
          Check <strong>{pendingReset?.email}</strong> for the 6-digit reset code.
        </Alert>
        {error && <Alert severity="error">{error}</Alert>}
        <TextField
          label="Reset Code"
          value={otp}
          onChange={e => setOtp(e.target.value.replace(/\D/g, '').slice(0, 6))}
          fullWidth required disabled={loading}
          inputProps={{ inputMode: 'numeric', maxLength: 6 }}
          placeholder="6-digit code"
        />
        <TextField label="New Password" type="password" value={password} onChange={e => setPassword(e.target.value)}
          fullWidth required disabled={loading}
          InputProps={{ startAdornment: <InputAdornment position="start"><LockIcon color="action" /></InputAdornment> }} />
        <TextField label="Confirm New Password" type="password" value={confirmPassword} onChange={e => setConfirmPassword(e.target.value)}
          fullWidth required disabled={loading}
          InputProps={{ startAdornment: <InputAdornment position="start"><LockIcon color="action" /></InputAdornment> }} />
        <Button type="submit" variant="contained" size="large" fullWidth disabled={loading || otp.length !== 6}>
          {loading ? <CircularProgress size={24} /> : 'Reset Password & Sign In'}
        </Button>
        <Stack direction="row" justifyContent="space-between" alignItems="center">
          <Button variant="text" onClick={() => setMode('forgot')} sx={{ color: 'text.secondary' }}>← Change email</Button>
          <Button variant="text" size="small" onClick={handleForgotResend} disabled={resendCooldown > 0 || otpLoading} sx={{ color: 'primary.main' }}>
            {resendCooldown > 0 ? `Resend in ${resendCooldown}s` : 'Resend code'}
          </Button>
        </Stack>
      </Box>
    );
  }

  return (
    <Box component="form" onSubmit={handleLogin} sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      <Typography variant="h6" align="center" fontWeight={700}>Sign In</Typography>
      {error && <Alert severity="error">{error}</Alert>}
      <TextField label="Email" type="email" value={email} onChange={e => setEmail(e.target.value)} fullWidth required disabled={loading}
        InputProps={{ startAdornment: <InputAdornment position="start"><EmailIcon color="action" /></InputAdornment> }} />
      <TextField label="Password" type="password" value={password} onChange={e => setPassword(e.target.value)} fullWidth required disabled={loading}
        InputProps={{ startAdornment: <InputAdornment position="start"><LockIcon color="action" /></InputAdornment> }} />
      <Button type="submit" variant="contained" size="large" fullWidth disabled={loading}>
        {loading ? <CircularProgress size={24} /> : 'Sign In'}
      </Button>
      <Stack direction="row" justifyContent="space-between" alignItems="center">
        <Button variant="text" onClick={() => setMode('email')} sx={{ color: 'text.secondary' }}>← Change email</Button>
        <Stack direction="row" spacing={0.5}>
          <Button variant="text" size="small" onClick={() => { setError(''); setMode('forgot'); }} sx={{ color: 'text.secondary' }}>
            Forgot password?
          </Button>
          <Button variant="text" onClick={() => setMode('signup')} sx={{ color: 'primary.main' }}>Create account</Button>
        </Stack>
      </Stack>
    </Box>
  );
}
// ─────────────────────────────────────────────────────────────────────────────

const Auth = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const navigate = useNavigate();
  const { login } = useAuth();
  const { showNotification } = useNotification();
  const [activeImageIndex, setActiveImageIndex] = useState(0);
  const [step, setStep] = useState('email_auth'); // email_auth | mobile | otp | password | create_password | create_username
  const [loading, setLoading] = useState(false);
  const [mobile, setMobile] = useState('');
  const [mobileCountryCode, setMobileCountryCode] = useState('+44');
  const [otp, setOtp] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState(null);

  useEffect(() => {
    const interval = setInterval(() => {
      setActiveImageIndex((prevIndex) =>
        prevIndex === splashImages.images.length - 1 ? 0 : prevIndex + 1
      );
    }, 6000);

    return () => clearInterval(interval);
  }, []);

  // Pre-warm the invisible reCAPTCHA as soon as the phone step is shown so
  // it has time to score the session before the user hits submit.
  useEffect(() => {
    if (step === 'mobile' && isFirebaseSms()) {
      initRecaptcha();
    }
  }, [step]);

  const handleMobileSubmit = async (mobileNumber, countryCode = '+44') => {
    try {
      setLoading(true);
      const { exists } = await api.checkCustomer(mobileNumber, countryCode);
      setMobile(mobileNumber);
      setMobileCountryCode(countryCode);
      if (exists) {
        setStep('password');
      } else if (isFirebaseSms()) {
        // reCAPTCHA already pre-warmed by useEffect above.
        await sendPhoneOtp(`${countryCode}${mobileNumber}`);
        setStep('otp');
      } else {
        const response = await api.sendOTP(mobileNumber, countryCode);
        if (response.message) showNotification(response.message, 'success');
        setStep('otp');
      }
    } catch (err) {
      showNotification(err.message || 'An error occurred. Please try again.', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleOTPSubmit = async (otpValue) => {
    setError(null);
    setLoading(true);
    try {
      if (!/^\d{6}$/.test(otpValue)) { showNotification('Please enter a valid 6-digit OTP', 'error'); return; }
      setOtp(otpValue);

      if (isFirebaseSms()) {
        // Firebase path: verify OTP client-side, get idToken, authenticate with backend.
        const firebaseIdToken = await verifyPhoneOtp(otpValue);
        const response = await api.loginFirebasePhone(mobile, mobileCountryCode, firebaseIdToken);
        await login(response);
        if (response.needs_password) {
          setStep('create_password');
        } else {
          navigate('/profile');
        }
      } else {
        // Custom OTP path: backend verifies OTP against DB.
        localStorage.setItem('otp', otpValue);
        const response = await api.loginOTP(mobile, otpValue, mobileCountryCode);
        if (response.token) {
          await login(response);
          setStep('create_password');
        } else {
          setStep('create_password');
        }
      }
    } catch (err) {
      if (err.status === 401 || err.code === 'auth/invalid-verification-code') {
        showNotification('Invalid OTP. Please try again.', 'error');
      } else {
        showNotification(err.message || 'An error occurred while verifying OTP', 'error');
      }
    } finally {
      setLoading(false);
    }
  };

  const handlePasswordSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    setLoading(true);
    try {
      if (password.length < 6) { showNotification('Password must be at least 6 characters long', 'error'); return; }
      const hashedPassword = md5(password);
      if (step === 'create_password') {
        if (isFirebaseSms()) {
          // Firebase path: JWT already set from loginFirebasePhone; no OTP needed.
          await api.createPasswordJwt(mobile, hashedPassword, mobileCountryCode);
          showNotification('Password set successfully', 'success');
          setStep('create_username');
        } else {
          // Custom OTP path: use OTP stored in localStorage for verification.
          const storedOTP = localStorage.getItem('otp');
          if (!storedOTP) { showNotification('OTP not found. Please try again.', 'error'); return; }
          const response = await api.createPassword(mobile, hashedPassword, storedOTP, mobileCountryCode);
          if (response.message === 'Password updated successfully') {
            showNotification(response.message, 'success');
            const loginResponse = await api.loginPassword(mobile, hashedPassword, mobileCountryCode);
            if (loginResponse.token) {
              api.setToken(loginResponse.token);
              await login(loginResponse);
              setStep('create_username');
            }
          }
        }
      } else {
        const response = await api.loginPassword(mobile, hashedPassword, mobileCountryCode);
        api.setToken(response.token);
        await login(response);
        navigate('/profile');
      }
    } catch (err) {
      if (err.status === 401) showNotification('Invalid password. Please try again.', 'error');
      else showNotification(err.message || 'An error occurred', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleOTPResend = async () => {
    try {
      setLoading(true);
      const response = await api.sendOTP(mobile, mobileCountryCode);
      if (response.message) showNotification(response.message, 'success');
    } catch (err) {
      showNotification(err.message || 'An error occurred. Please try again.', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handlePasskeyLogin = async (phoneNumber) => {
    try {
      setLoading(true);
      const options = await api.passkeyLoginStart(phoneNumber);
      const publicKey = {
        ...options,
        challenge: b64urlToBuffer(options.challenge),
        allowCredentials: (options.allowCredentials || []).map(c => ({ ...c, id: b64urlToBuffer(c.id) })),
      };
      const assertion = await navigator.credentials.get({ publicKey });
      const response = await api.passkeyLoginComplete(phoneNumber, assertionToJSON(assertion));
      if (response.token) {
        api.setToken(response.token);
        await login(response);
        navigate('/profile');
      }
    } catch (err) {
      if (err.name === 'NotAllowedError') showNotification('Biometric authentication was cancelled.', 'warning');
      else showNotification(err.message || 'Passkey authentication failed.', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleForgotPassword = async () => {
    try {
      setLoading(true);
      const response = await api.sendOTP(mobile, mobileCountryCode);
      if (response.message) showNotification(response.message, 'success');
      setStep('otp');
    } catch (err) {
      showNotification(err.message || 'An error occurred. Please try again.', 'error');
    } finally {
      setLoading(false);
    }
  };

  // Gets a Google access token via whichever mechanism actually works on this
  // platform - native Play Services sign-in on native (see loadGis's matching
  // comment for why the web popup flow can't work there), the existing
  // popup-based token client in a browser/PWA.
  const getGoogleAccessToken = useCallback(async () => {
    if (Capacitor.isNativePlatform()) {
      // GoogleSignInClient caches the last-used account and reuses it
      // silently on the next signIn() call, skipping the account chooser.
      // Signing out first clears that cache so the picker always shows.
      await GoogleAuth.signOut().catch(() => {});
      const result = await GoogleAuth.signIn();
      const accessToken = result?.authentication?.accessToken;
      if (!accessToken) throw new Error('Google sign-in did not return an access token.');
      return accessToken;
    }
    await loadGis();
    const clientId = process.env.REACT_APP_GOOGLE_CLIENT_ID;
    if (!clientId) throw new Error('Google Sign-In is not configured.');
    return new Promise((resolve, reject) => {
      const client = window.google.accounts.oauth2.initTokenClient({
        client_id: clientId,
        scope: 'openid email profile',
        callback: (tokenResponse) => {
          if (tokenResponse.error) { reject(new Error(tokenResponse.error)); return; }
          resolve(tokenResponse.access_token);
        },
      });
      client.requestAccessToken({ prompt: '' });
    });
  }, []);

  const handleGoogleLogin = useCallback(async () => {
    try {
      setLoading(true);
      const accessToken = await getGoogleAccessToken();
      // Try to sign in with google auth (existing user)
      const response = await api.googleAuth(accessToken);
      if (response.token) {
        api.setToken(response.token);
        await login(response);
        if (response.is_new_user) {
          setStep('create_username');
        } else {
          navigate('/profile');
        }
      }
    } catch (err) {
      if (err.status === 401) {
        showNotification('No account found. Sign up via phone first, then link Google in Profile → Account.', 'info');
      } else {
        showNotification(err.message || 'Google sign-in failed.', 'error');
      }
    } finally {
      setLoading(false);
    }
  }, [login, navigate, showNotification, setStep, getGoogleAccessToken]);

  const handleEmailSignup = (email, password, name, otp) => {
    setLoading(true);
    api.emailSignup(email, password, name, otp)
      .then(async (response) => { await login(response); setStep('create_username'); })
      .catch(err => showNotification(err.message || 'Sign up failed.', 'error'))
      .finally(() => setLoading(false));
  };

  const handleEmailLogin = (email, password) => {
    setLoading(true);
    api.emailLogin(email, password)
      .then(async (response) => { await login(response); navigate('/profile'); })
      .catch(err => showNotification(err.message || 'Sign in failed.', 'error'))
      .finally(() => setLoading(false));
  };

  const handleEmailForgotReset = (email, otp, password) => {
    setLoading(true);
    api.emailResetPassword(email, otp, password)
      .then(async (response) => { await login(response); navigate('/profile'); })
      .catch(err => showNotification(err.message || 'Password reset failed.', 'error'))
      .finally(() => setLoading(false));
  };

  const renderStep = () => {
    switch (step) {
      case 'mobile':
        return (
          <MobileInput
            onSubmit={handleMobileSubmit}
            onPasskeyLogin={handlePasskeyLogin}
            onGoogleLogin={handleGoogleLogin}
            onEmailSignup={() => setStep('email_auth')}
            loading={loading}
          />
        );
      case 'otp':
        return (
          <OTPInput
            onSubmit={handleOTPSubmit}
            onResend={handleOTPResend}
            loading={loading}
            mobile={mobile}
            otp={otp}
            setOtp={setOtp}
            error={error}
          />
        );
      case 'password':
        return (
          <LoginForm
            onSubmit={handlePasswordSubmit}
            loading={loading}
            mobile={mobile}
            onForgotPassword={handleForgotPassword}
            password={password}
            setPassword={setPassword}
            error={error}
          />
        );
      case 'create_password':
        return (
          <RegisterForm
            onSubmit={handlePasswordSubmit}
            loading={loading}
            mobile={mobile}
            isCreatePassword={true}
            password={password}
            setPassword={setPassword}
            error={error}
          />
        );
      case 'create_username':
        return (
          <CreateUsername
            onContinue={() => navigate('/profile')}
            onSkip={() => navigate('/profile')}
          />
        );
      case 'email_auth':
        return (
          <EmailAuthForm
            onSignup={handleEmailSignup}
            onLogin={handleEmailLogin}
            onForgotReset={handleEmailForgotReset}
            onPhoneLogin={() => setStep('mobile')}
            onPasskeyLogin={handlePasskeyLogin}
            onGoogleLogin={handleGoogleLogin}
            loading={loading}
          />
        );
      default:
        return null;
    }
  };

  const renderSplashCarousel = () => (
    <Box
      component={motion.div}
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.5 }}
      sx={{ height: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', p: 4, position: 'relative', overflow: 'hidden' }}
    >
      <Box sx={{ maxWidth: 600, textAlign: 'center', mb: 6 }}>
        <Typography
          component={motion.h1}
          initial={{ y: -20, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ delay: 0.2, duration: 0.5 }}
          variant="h4"
          sx={{ mb: 2, fontWeight: 700, background: 'linear-gradient(45deg, #2196F3 30%, #3F51B5 90%)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}
        >
          Your Events, Understood
        </Typography>
        <Typography
          component={motion.p}
          initial={{ y: 20, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ delay: 0.4, duration: 0.5 }}
          variant="h5"
          sx={{ mb: 4, color: 'text.primary', fontWeight: 'normal', lineHeight: 1.5 }}
        >
          Search by memory. Share by relationship.
        </Typography>
      </Box>
      <Box sx={{ width: '100%', maxWidth: 500, aspectRatio: '1', position: 'relative', mb: 4 }}>
        {splashImages.images.map((image, index) => (
          <Box
            key={index}
            component={motion.div}
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: index === activeImageIndex ? 1 : 0, scale: index === activeImageIndex ? 1 : 0.9 }}
            transition={{ duration: 0.5 }}
            sx={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', '& img': { width: '100%', height: '100%', objectFit: 'cover', borderRadius: 2, boxShadow: theme.shadows[8] } }}
          >
            <img src={resolveStoreAssetUrl(image.url)} alt={image.title} />
          </Box>
        ))}
      </Box>
    </Box>
  );

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '100vh' }}>
      <Header />
      <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', bgcolor: 'grey.50' }}>
        <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, flex: 1 }}>
          {!isMobile && (
            <Box sx={{ flex: 3, display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
              {renderSplashCarousel()}
            </Box>
          )}
          <Box
            component={motion.div}
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.5 }}
            sx={{ flex: 2, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', p: 3 }}
          >
            <Paper
              component={motion.div}
              initial={{ y: 20, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ delay: 0.2, duration: 0.5 }}
              elevation={3}
              sx={{ width: '100%', maxWidth: 400, p: 0, borderRadius: 2, overflow: 'hidden' }}
            >
              <Box sx={{ p: 3 }}>
                {renderStep()}
                {(step === 'email_auth' || step === 'mobile' || step === 'password' || step === 'create_password') && (
                  <Typography variant="caption" color="text.secondary" align="center" sx={{ display: 'block', mt: 2.5 }}>
                    By continuing, you agree to our{' '}
                    <Link href="/terms.html" target="_blank" rel="noopener noreferrer">Terms &amp; Conditions</Link>
                    {' '}and{' '}
                    <Link href="/privacy.html" target="_blank" rel="noopener noreferrer">Privacy Policy</Link>.
                  </Typography>
                )}
              </Box>
            </Paper>
          </Box>
        </Box>
        <Container maxWidth={false} sx={{ py: 4 }}>
          <Box
            component={motion.div}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4, duration: 0.5 }}
            sx={{ display: 'grid', gridTemplateColumns: { xs: 'repeat(2, 1fr)', md: 'repeat(4, 1fr)' }, gap: { xs: 3, md: 6 }, width: '100%', px: { xs: 2, md: 4 } }}
          >
            <Box sx={{ textAlign: 'left' }}>
              <Typography variant="subtitle1" sx={{ mb: 0.5, color: 'primary.main', fontWeight: 600 }}>AI Moments Search</Typography>
              <Typography variant="body2" color="text.secondary">Find any photo by describing the moment, person, emotion, or setting</Typography>
            </Box>
            <Box sx={{ textAlign: 'left' }}>
              <Typography variant="subtitle1" sx={{ mb: 0.5, color: 'primary.main', fontWeight: 600 }}>Smart Face Memory</Typography>
              <Typography variant="body2" color="text.secondary">Name people once and recognise them across years of events</Typography>
            </Box>
            <Box sx={{ textAlign: 'left' }}>
              <Typography variant="subtitle1" sx={{ mb: 0.5, color: 'primary.main', fontWeight: 600 }}>Smart Circles</Typography>
              <Typography variant="body2" color="text.secondary">Share the right photos with family, work, friends, or VIP guests</Typography>
            </Box>
            <Box sx={{ textAlign: 'left' }}>
              <Typography variant="subtitle1" sx={{ mb: 0.5, color: 'primary.main', fontWeight: 600 }}>Event Intelligence</Typography>
              <Typography variant="body2" color="text.secondary">Understand who was present, what happened, and how everyone connects</Typography>
            </Box>
          </Box>
        </Container>
      </Box>
      <Footer />
    </Box>
  );
};

export default Auth;
